// src/features/admin/pages/AdminKeywordsPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminRole } from "../AdminGuard";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

type Keyword = { id: number; keyword: string; created_at: string };

export default function AdminKeywordsPage() {
    const { roles } = useAdminRole();
    const nav = useNavigate();
    useEffect(() => {
        if (roles.length > 0 && !roles.some(r => r === "ADMIN" || r === "SUPER_ADMIN")) {
            nav("/admin/tox", { replace: true });
        }
    }, [roles, nav]);
    const [keywords, setKeywords] = useState<Keyword[]>([]);
    const [loading, setLoading] = useState(true);
    const [newText, setNewText] = useState("");
    const [adding, setAdding] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [editText, setEditText] = useState("");
    const [error, setError] = useState("");
    const editInputRef = useRef<HTMLInputElement>(null);

    const load = () => {
        setLoading(true);
        api("/admin/keywords")
            .then(d => setKeywords(d.keywords || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    useEffect(() => {
        if (editId !== null) editInputRef.current?.focus();
    }, [editId]);

    const add = async () => {
        const text = newText.trim();
        if (!text) return;
        setAdding(true);
        setError("");
        try {
            const row = await api("/admin/keywords", { method: "POST", json: { keyword: text } });
            setKeywords(prev => [...prev, row]);
            setNewText("");
        } catch (e: any) {
            setError(e?.code === "DUPLICATE_KEYWORD" ? "이미 존재하는 키워드입니다." : "추가 실패");
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (kw: Keyword) => {
        setEditId(kw.id);
        setEditText(kw.keyword);
        setError("");
    };

    const saveEdit = async (id: number) => {
        const text = editText.trim();
        if (!text) return;
        setError("");
        try {
            const row = await api(`/admin/keywords/${id}`, { method: "PATCH", json: { keyword: text } });
            setKeywords(prev => prev.map(k => k.id === id ? { ...k, keyword: row.keyword } : k));
            setEditId(null);
        } catch (e: any) {
            setError(e?.code === "DUPLICATE_KEYWORD" ? "이미 존재하는 키워드입니다." : "수정 실패");
        }
    };

    const remove = async (id: number) => {
        if (!confirm("이 키워드를 삭제하면 해당 키워드를 선택한 리뷰에도 반영됩니다. 삭제할까요?")) return;
        try {
            await api(`/admin/keywords/${id}`, { method: "DELETE" });
            setKeywords(prev => prev.filter(k => k.id !== id));
        } catch {
            setError("삭제 실패");
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 600 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: FW.extraBold }}>리뷰 키워드 관리</h2>

            {/* Add form */}
            <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, padding: "20px 22px" }}>
                <div style={{ fontSize: 14, fontWeight: FW.bold, marginBottom: 12, color: COLOR.textDark }}>키워드 추가</div>
                <div style={{ display: "flex", gap: 8 }}>
                    <input
                        value={newText}
                        onChange={e => setNewText(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && add()}
                        placeholder="새 키워드 입력"
                        maxLength={100}
                        style={{
                            flex: 1, padding: "8px 12px", fontSize: 14,
                            border: BORDER.lightAlt, borderRadius: RADIUS.sm, outline: "none",
                        }}
                    />
                    <button
                        onClick={add}
                        disabled={adding || !newText.trim()}
                        style={{
                            padding: "8px 18px", fontSize: 13, fontWeight: FW.bold,
                            background: adding || !newText.trim() ? "#e4e4e7" : COLOR.textDark,
                            color: adding || !newText.trim() ? COLOR.textLight : COLOR.white,
                            border: "none", borderRadius: RADIUS.sm, cursor: adding || !newText.trim() ? "default" : "pointer",
                        }}
                    >
                        추가
                    </button>
                </div>
                {error && <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626" }}>{error}</div>}
            </div>

            {/* Keyword list */}
            <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, overflow: "hidden" }}>
                <div style={{ padding: "14px 22px", borderBottom: "1px solid #f4f4f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 14, fontWeight: FW.bold, color: COLOR.textDark }}>키워드 목록</span>
                    <span style={{ fontSize: 12, color: COLOR.textMuted }}>{keywords.length}개</span>
                </div>

                {loading ? (
                    <div style={{ padding: 24, textAlign: "center", color: COLOR.textLight, fontSize: 13 }}>불러오는 중...</div>
                ) : keywords.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", color: COLOR.textLight, fontSize: 13 }}>등록된 키워드가 없습니다.</div>
                ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {keywords.map((kw, i) => (
                            <li
                                key={kw.id}
                                style={{
                                    display: "flex", alignItems: "center", gap: 12,
                                    padding: "12px 22px",
                                    borderBottom: i < keywords.length - 1 ? "1px solid #f4f4f5" : "none",
                                }}
                            >
                                {/* Order badge */}
                                <span style={{
                                    minWidth: 24, height: 24, borderRadius: "50%",
                                    background: COLOR.bgLight, fontSize: 11, fontWeight: FW.bold,
                                    color: COLOR.textMuted, display: "flex", alignItems: "center",
                                    justifyContent: "center", flexShrink: 0,
                                }}>
                                    {i + 1}
                                </span>

                                {/* Edit inline or label */}
                                {editId === kw.id ? (
                                    <input
                                        ref={editInputRef}
                                        value={editText}
                                        onChange={e => setEditText(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") saveEdit(kw.id);
                                            if (e.key === "Escape") setEditId(null);
                                        }}
                                        maxLength={100}
                                        style={{
                                            flex: 1, padding: "5px 10px", fontSize: 14,
                                            border: "1px solid #a1a1aa", borderRadius: RADIUS.xs, outline: "none",
                                        }}
                                    />
                                ) : (
                                    <span style={{ flex: 1, fontSize: 14, color: COLOR.textDark }}>{kw.keyword}</span>
                                )}

                                {/* Action buttons */}
                                {editId === kw.id ? (
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <Btn color={COLOR.textDark} onClick={() => saveEdit(kw.id)}>저장</Btn>
                                        <Btn color={COLOR.textMuted} onClick={() => setEditId(null)}>취소</Btn>
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <Btn color="#2563eb" onClick={() => startEdit(kw)}>수정</Btn>
                                        <Btn color="#dc2626" onClick={() => remove(kw.id)}>삭제</Btn>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function Btn({ children, onClick, color }: { children: React.ReactNode; onClick: () => void; color: string }) {
    return (
        <button
            onClick={onClick}
            style={{
                padding: "5px 12px", fontSize: 12, fontWeight: FW.bold,
                background: "transparent", color, border: `1px solid ${color}`,
                borderRadius: RADIUS.xs, cursor: "pointer",
            }}
        >
            {children}
        </button>
    );
}